import CapacitorStorage from 'Utils/CapacitorStorage';
import { reportError } from 'Utils/reportError';

/**
 * A setting kept in the per-user namespace of the app's persisted storage
 * (`<user id>-<storage key>`), so each account on a device keeps its own value.
 * Such a setting is a device preference, not account data: sign-out keeps the
 * key through its storage clear (`isStorageKey` says which keys those are).
 */
export interface PerUserDeviceStore<T> {
  storageKey: string;
  /** Whether a full storage key (any user prefix included) holds this setting. */
  isStorageKey(storageKey: string): boolean;
  /** A user's saved value, or the decoded default when none is saved or readable. */
  load(userId: string | undefined): Promise<T>;
  save(userId: string | undefined, value: T): Promise<void>;
}

export interface PerUserDeviceStoreOptions<T> {
  storageKey: string;
  /** The value from whatever was read back; must return the default when unreadable. */
  decode(stored: unknown): T;
  encode(value: T): string;
  /** The tag a failed read is reported under. */
  errorTag: string;
}

export const createPerUserDeviceStore = <T>({
  storageKey,
  decode,
  encode,
  errorTag,
}: PerUserDeviceStoreOptions<T>): PerUserDeviceStore<T> => {
  if (typeof storageKey !== 'string' || storageKey === '') {
    throw new Error('A per-user device store needs a storage key');
  }
  return {
    storageKey,
    isStorageKey: (fullKey: string): boolean =>
      fullKey === storageKey || fullKey.endsWith(`-${storageKey}`),
    load: async (userId: string | undefined): Promise<T> => {
      try {
        return decode(await CapacitorStorage.getItemFor(userId, storageKey));
      } catch (e) {
        reportError(e, errorTag);
        return decode(undefined);
      }
    },
    save: async (userId: string | undefined, value: T): Promise<void> => {
      await CapacitorStorage.setItemFor(userId, storageKey, encode(value));
    },
  };
};
